import {intlLocale} from './intl-format';

/**
 * Countries as ISO 3166-1 alpha-2 codes, the only form Stripe takes for an
 * address (and for the tax it works out from it), with their names in the
 * app's language from Intl.DisplayNames -- no strings of our own.
 *
 * The billing details took the country as free text, so customers typed
 * "Spain" or "Deutschland" and that reached Stripe as the address country.
 * The form now picks from this list, and a value typed before is read back to
 * a code where it can be (names in the app's nine languages, common short
 * forms). The API has the same list and matching (functions/src/common/util/countries.ts).
 */

export const COUNTRY_CODES: string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];

const CODE_SET = new Set(COUNTRY_CODES);

/** The app's languages as Intl knows them. */
const NAME_LOCALES = ['en', 'de', 'es', 'fr', 'hi', 'id', 'ja', 'pt', 'ru'];

/** What people type that no country's name says. */
const ALIASES: {[alias: string]: string} = {
  'uk': 'GB', 'u k': 'GB', 'great britain': 'GB', 'britain': 'GB', 'england': 'GB', 'scotland': 'GB',
  'wales': 'GB', 'northern ireland': 'GB', 'gbr': 'GB',
  'usa': 'US', 'u s a': 'US', 'u s': 'US', 'united states of america': 'US', 'america': 'US',
  'holland': 'NL', 'the netherlands': 'NL',
  'esp': 'ES', 'deu': 'DE', 'fra': 'FR', 'ita': 'IT', 'prt': 'PT', 'nld': 'NL', 'bel': 'BE',
  'aut': 'AT', 'che': 'CH', 'irl': 'IE', 'swe': 'SE', 'nor': 'NO', 'dnk': 'DK', 'fin': 'FI',
  'pol': 'PL', 'can': 'CA', 'mex': 'MX', 'aus': 'AU', 'nzl': 'NZ', 'ind': 'IN', 'bra': 'BR',
  'arg': 'AR', 'jpn': 'JP', 'rus': 'RU', 'idn': 'ID', 'zaf': 'ZA', 'are': 'AE',
};

/** Lowercase, accents and punctuation dropped: "España" and "espana" match. */
function normalize(text: string): string {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[.,'’()\-_/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

let byName: Map<string, string> | null = null;

function nameIndex(): Map<string, string> {
  if (byName) {
    return byName;
  }
  const index = new Map<string, string>();
  for (const locale of NAME_LOCALES) {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([locale], {type: 'region'});
    } catch (e) {
      continue;
    }
    for (const code of COUNTRY_CODES) {
      let name = '';
      try {
        name = names.of(code) || '';
      } catch (e) {
        name = '';
      }
      const key = normalize(name);
      if (key && key !== code.toLowerCase() && !index.has(key)) {
        index.set(key, code);
      }
    }
  }
  for (const alias of Object.keys(ALIASES)) {
    index.set(alias, ALIASES[alias]);
  }
  byName = index;
  return index;
}

/**
 * The ISO code for what is on file: a code already, or a country's name in
 * one of the app's languages ("Spain", "Deutschland", "UK"). '' when it
 * cannot be told -- the form then asks for the country.
 */
export function countryCode(value: any): string {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  const upper = raw.toUpperCase();
  if (upper.length === 2 && CODE_SET.has(upper)) {
    return upper;
  }
  return nameIndex().get(normalize(raw)) || '';
}

const namers = new Map<string, Intl.DisplayNames | null>();

function namer(lang: string): Intl.DisplayNames | null {
  const locale = intlLocale(lang);
  if (!namers.has(locale)) {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([locale, 'en'], {type: 'region'});
    } catch (e) {
      names = null;
    }
    namers.set(locale, names);
  }
  return namers.get(locale) || null;
}

/** "Spain" / "España" / "スペイン" for ES, in the app's language; what is on file when it is not a country. */
export function countryName(value: any, lang: string): string {
  const code = countryCode(value);
  if (!code) {
    return String(value || '');
  }
  const names = namer(lang);
  try {
    return (names && names.of(code)) || code;
  } catch (e) {
    return code;
  }
}

const optionLists = new Map<string, {code: string, name: string}[]>();

/** Every country as {code, name}, sorted by name the way the app's language sorts. */
export function countryOptions(lang: string): {code: string, name: string}[] {
  const locale = intlLocale(lang);
  let list = optionLists.get(locale);
  if (!list) {
    let collator: Intl.Collator | null = null;
    try {
      collator = new Intl.Collator(locale);
    } catch (e) {
      collator = null;
    }
    list = COUNTRY_CODES.map(code => ({code, name: countryName(code, lang)}))
      .sort((a, b) => collator ? collator.compare(a.name, b.name) : a.name.localeCompare(b.name));
    optionLists.set(locale, list);
  }
  return list;
}

/**
 * How a tax / VAT number is written in the billing country, as the field's
 * example: the API reads the number by the country (functions tax-ids.ts),
 * so the example follows it. '' where there is no single format.
 */
const TAX_ID_EXAMPLES: {[code: string]: string} = {
  AT: 'ATU12345678', BE: 'BE0123456789', BG: 'BG123456789', CY: 'CY12345678L', CZ: 'CZ12345678',
  DE: 'DE123456789', DK: 'DK12345678', EE: 'EE123456789', ES: 'B12345678', FI: 'FI12345678',
  FR: 'FR12345678901', GR: 'EL123456789', HR: 'HR12345678901', HU: 'HU12345678', IE: 'IE1234567T',
  IT: 'IT12345678901', LT: 'LT123456789', LU: 'LU12345678', LV: 'LV12345678901', MT: 'MT12345678',
  NL: 'NL123456789B01', PL: 'PL1234567890', PT: 'PT123456789', RO: 'RO1234567', SE: 'SE123456789001',
  SI: 'SI12345678', SK: 'SK1234567890', GB: 'GB123456789', CH: 'CHE-123.456.789 MWST', NO: '123456789MVA',
  US: '12-3456789', IN: '22AAAAA0000A1Z5', AU: '51824753556', NZ: '123456789', CA: '123456789RT0001',
};

export function taxIdExample(country: any): string {
  return TAX_ID_EXAMPLES[countryCode(country)] || '';
}
